import { createContext, useState, useContext, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { saveCountriesToDatabase } from "../hooks/api";

const CountryContext = createContext(null);

// Función auxiliar para obtener la key de localStorage según el usuario
const getStorageKey = (childId) => {
  return childId ? `selectedCountries_${childId}` : "selectedCountries";
};

// Función para obtener países desde localStorage
const getCountriesFromStorage = (childId) => {
  const key = getStorageKey(childId);
  const stored = localStorage.getItem(key);
  return stored ? JSON.parse(stored) : [];
};

// Función para guardar países en localStorage
const saveCountriesToStorage = (childId, countries) => {
  const key = getStorageKey(childId);
  localStorage.setItem(key, JSON.stringify(countries));
};

export const CountryProvider = ({ children }) => {
  // 1) Inicializar selectedCountries desde localStorage en el propio useState
  const [selectedCountries, setSelectedCountries] = useState(() => {
    const childId = localStorage.getItem("childId");
    return getCountriesFromStorage(childId);
  });

  // 2) Cuando cambia childId, cargar países de ese usuario específico
  useEffect(() => {
    const syncFromLocalStorage = () => {
      const childId = localStorage.getItem("childId");
      const countries = getCountriesFromStorage(childId);
      console.log(`🌍 Sincronizando países para usuario ${childId}:`, countries);

      if (JSON.stringify(countries) !== JSON.stringify(selectedCountries)) {
        console.log(`🌍 Actualizando países (${countries.length} países encontrados)`);
        setSelectedCountries(countries);
      } else {
        console.log(`🌍 Países sin cambios`);
      }
    };

    // Escucha cambios en childId (por login, logout, etc)
    const onStorage = (e) => {
      if (e.key === "childId") {
        syncFromLocalStorage();
      }
    };
    window.addEventListener("storage", onStorage);

    // También ejecuta al montar (por si childId ya cambió antes de montar)
    syncFromLocalStorage();

    return () => window.removeEventListener("storage", onStorage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localStorage.getItem("childId")]);

  // 3) Sincroniza cambios de selectedCountries a local+API
  useEffect(() => {
    const childId = localStorage.getItem("childId");

    // Guardar en localStorage con key específica del usuario
    saveCountriesToStorage(childId, selectedCountries);

    // Solo sincronizar con backend si hay childId
    if (!childId) return;

    saveCountriesToDatabase(childId, selectedCountries).catch(() =>
      console.log("⚠️  Backend no disponible, usando solo localStorage")
    );
  }, [selectedCountries]);

  const addCountry = (country) => {
    setSelectedCountries((prev) => {
      if (prev.some((c) => c.country === country.country)) return prev;
      return [...prev, country];
    });
  };

  const removeCountry = (countryId) => {
    setSelectedCountries((prev) => prev.filter((c) => c.id !== countryId));
  };

  const clearCountries = () => {
    const childId = localStorage.getItem("childId");
    const key = getStorageKey(childId);
    localStorage.removeItem(key);
    setSelectedCountries([]);
  };

  const value = useMemo(
    () => ({
      selectedCountries,
      addCountry,
      removeCountry,
      clearCountries,
      setSelectedCountries,
    }),
    [selectedCountries]
  );

  return (
    <CountryContext.Provider value={value}>{children}</CountryContext.Provider>
  );
};

export const useCountry = () => {
  const context = useContext(CountryContext);
  if (context === null) {
    throw new Error("useCountry must be used within a CountryProvider");
  }
  return context;
};

CountryProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
